/**
 * Zwergenwald - Kopf-Ergaenzung "A" (sections/zw-kopf-a.liquid, assets/zw-kopf.css).
 *
 * Ergaenzt Horizons Kopf (<header-component>), ersetzt ihn nicht:
 *  - markiert die Hauptmenue-Links: Produkt-Links bekommen die Klasse zw-prod (weisse Pille mit Preis oder "bald"),
 *    alle anderen zw-sek (kleiner, zurueckgenommen);
 *  - Computer (ab 750 px, mit Maus): beim Ueberfahren oder Fokussieren eines Produkt-Links klappt darunter genau die
 *    Karte dieses Produkts auf - Bild, Kurztext, Preis (Inhalt aus dem <template data-zw-aufklapper> des Abschnitts);
 *  - Handy: Horizons Menue-Knopf oeffnet statt der Schublade ein eigenes Blatt unter dem Kopf - Produktkarten, kleine
 *    Links, grosser Warenkorb-Knopf (<template data-zw-blatt>, haengt am <body>); Horizons Schublade bleibt per CSS unsichtbar.
 *
 * Nichts davon laedt Bilder, bevor es gebraucht wird (Karten stecken in <template>).
 * Fehlt etwas (kein Kopf, kein Template), tut das Skript nichts - Horizons Kopf bleibt, wie er ist.
 */
class ZwKopfA extends HTMLElement {
  /** @type {HTMLElement | null} */ #kopf = null;
  /** @type {HTMLElement | null} */ #panel = null;
  /** @type {number | undefined} */ #schliessTimer;
  /** @type {HTMLElement | null} */ #blatt = null;
  /** @type {boolean} */ #blattOffen = false;
  #handy = window.matchMedia('(max-width: 749px)');
  #computer = window.matchMedia('(min-width: 750px) and (hover: hover)');

  connectedCallback() {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => this.#start(), { once: true });
    else this.#start();
  }

  #start() {
    this.#kopf = document.querySelector('header-component');
    if (!this.#kopf) return;

    this.#markieren();
    // Horizon baut die Menueliste bei Platzmangel um (overflow-list): Markierung bei Aenderungen wiederholen
    const menue = this.#kopf.querySelector('header-menu');
    if (menue) new MutationObserver(() => this.#markieren()).observe(menue, { childList: true, subtree: true });
    // Nach dem Laden der Schriften stimmen die Breiten erst endgueltig
    document.fonts?.ready.then(() => this.#neuBerechnen());

    // Computer: Karten vorab bauen, sobald der Browser frei ist - so ist das Bild beim ersten Ueberfahren schon da
    if (this.#computer.matches) {
      const bauen = () => this.#panelBauen();
      if ('requestIdleCallback' in window) window.requestIdleCallback(bauen, { timeout: 2500 });
      else window.setTimeout(bauen, 1500);
    }

    this.#kopf.addEventListener('pointerover', (e) => this.#ueberfahren(e));
    this.#kopf.addEventListener('focusin', (e) => this.#fokus(e));
    this.#kopf.addEventListener('pointerleave', () => this.#planeSchliessen());
    this.#kopf.addEventListener('focusout', (e) => {
      if (!(e.relatedTarget instanceof Node) || !this.#kopf?.contains(e.relatedTarget)) this.#planeSchliessen();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.#schliessen(); });
    // Fenster wird schmaler (Tablet drehen): gebautes Panel entfernen, sonst liegt es ohne Stil im Kopf
    this.#computer.addEventListener('change', (e) => {
      if (!e.matches && this.#panel) { this.#panel.remove(); this.#panel = null; }
    });

    const schublade = this.#kopf.querySelector('details.menu-drawer-container');
    if (schublade instanceof HTMLDetailsElement) this.#handyMenueAnbinden(schublade);
    this.#zaehlerSpiegeln();
  }

  /** @returns {Map<string, HTMLAnchorElement>} Karten des Aufklappers nach Pfad */
  #karten() {
    const tpl = this.querySelector('template[data-zw-aufklapper]');
    const karten = new Map();
    if (!(tpl instanceof HTMLTemplateElement)) return karten;
    for (const a of tpl.content.querySelectorAll('a.zw-ak')) karten.set(new URL(a.href, location.href).pathname, /** @type {HTMLAnchorElement} */ (a));
    return karten;
  }

  #markieren() {
    if (!this.#kopf) return;
    const karten = this.#karten();
    const links = this.#kopf.querySelectorAll('header-menu a.menu-list__link');
    let ersteSek = true;
    let geaendert = false;
    for (const a of links) {
      if (!(a instanceof HTMLAnchorElement)) continue;
      const karte = karten.get(new URL(a.href, location.href).pathname);
      // Klassen und Daten haengen am Link selbst (data-skip-node-update schuetzt ihn, die <li> nicht)
      const vorher = a.className + '|' + (a.dataset.zwTag || '') + '|' + (a.dataset.zwPreis || '');
      if (karte) {
        a.classList.add('zw-prod');
        // Preis oder "bald" erscheinen per CSS als ::before
        if (karte.dataset.zwTag) {
          a.dataset.zwTag = karte.dataset.zwTag;
          delete a.dataset.zwPreis;
        } else if (karte.dataset.zwPreis) {
          a.dataset.zwPreis = karte.dataset.zwPreis;
        }
      } else {
        a.classList.add('zw-sek');
        a.classList.toggle('zw-sek-erste', ersteSek);
        ersteSek = false;
      }
      if (vorher !== a.className + '|' + (a.dataset.zwTag || '') + '|' + (a.dataset.zwPreis || '')) geaendert = true;
    }
    // Die Pillen sind breiter als Horizons Links: Horizons Platzberechnung ("Mehr") neu anstossen
    if (geaendert) this.#neuBerechnen();
  }

  #neuBerechnen() {
    window.requestAnimationFrame(() => {
      this.#kopf?.querySelector('header-menu overflow-list')?.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
    });
  }

  /** Aufklapper (nur Computer) */
  #panelBauen() {
    if (this.#panel) return this.#panel;
    const tpl = this.querySelector('template[data-zw-aufklapper]');
    const reihe = this.#kopf?.querySelector('.header__row--top');
    if (!(tpl instanceof HTMLTemplateElement) || !reihe) return null;
    const panel = /** @type {HTMLElement} */ (tpl.content.firstElementChild?.cloneNode(true));
    if (!panel) return null;
    panel.addEventListener('pointerenter', () => window.clearTimeout(this.#schliessTimer));
    panel.addEventListener('pointerleave', () => this.#planeSchliessen());
    reihe.appendChild(panel);
    this.#panel = panel;
    return panel;
  }

  /** @param {Event} e */
  #ueberfahren(e) {
    if (!this.#computer.matches) return;
    const ziel = e.target instanceof Element ? e.target.closest('a.zw-prod') : null;
    if (ziel instanceof HTMLAnchorElement) this.#oeffnen(ziel);
    else if (e.target instanceof Element && !e.target.closest('.zw-aufklapper')) this.#planeSchliessen();
  }

  /** @param {FocusEvent} e */
  #fokus(e) {
    if (!this.#computer.matches) return;
    const ziel = e.target instanceof Element ? e.target.closest('a.zw-prod') : null;
    if (ziel instanceof HTMLAnchorElement) this.#oeffnen(ziel);
    // Fokus wandert auf Logo, Nebenlinks oder Symbole: Karte zu
    else if (!(e.target instanceof Element && e.target.closest('.zw-aufklapper'))) this.#schliessen();
  }

  /** @param {HTMLAnchorElement} link */
  #oeffnen(link) {
    window.clearTimeout(this.#schliessTimer);
    const panel = this.#panelBauen();
    const reihe = this.#kopf?.querySelector('.header__row--top');
    if (!panel || !reihe) return;
    // Nur die Karte des ueberfahrenen Produkts zeigen
    const pfad = new URL(link.href, location.href).pathname;
    let gefunden = false;
    for (const k of panel.querySelectorAll('a.zw-ak')) {
      if (!(k instanceof HTMLAnchorElement)) continue;
      const passt = new URL(k.href, location.href).pathname === pfad;
      k.toggleAttribute('data-aktiv', passt);
      gefunden ||= passt;
    }
    if (!gefunden) return this.#schliessen();
    panel.setAttribute('data-offen', '');
    for (const p of this.#kopf?.querySelectorAll('a.zw-prod') ?? []) p.toggleAttribute('data-zw-offen', p === link);
    // Karte buendig unter der Pille, aber nie ueber den rechten Fensterrand hinaus. Horizon setzt die Kopfzeile als
    // Raster mit Seitenrand-Spalten, der Bezugspunkt des Panels ist deshalb nicht die Zeilenkante: erst auf 0 setzen,
    // die tatsaechliche Lage messen und dann um den Unterschied verschieben.
    panel.style.setProperty('--zw-ak-links', '0px');
    const null0 = panel.getBoundingClientRect().left;
    const ziel = Math.min(link.getBoundingClientRect().left, window.innerWidth - panel.offsetWidth - 16);
    panel.style.setProperty('--zw-ak-links', Math.round(Math.max(16, ziel) - null0) + 'px');
  }

  #planeSchliessen() {
    window.clearTimeout(this.#schliessTimer);
    this.#schliessTimer = window.setTimeout(() => this.#schliessen(), 180);
  }

  #schliessen() {
    window.clearTimeout(this.#schliessTimer);
    this.#panel?.removeAttribute('data-offen');
    for (const p of this.#kopf?.querySelectorAll('a.zw-prod[data-zw-offen]') ?? []) p.removeAttribute('data-zw-offen');
  }

  /**
   * Handy: Der Menue-Knopf (Horizons <summary>) oeffnet nicht Horizons Schublade, sondern das eigene Blatt, das direkt am
   * <body> haengt. In Horizons Schublade waere es im mitfahrenden Kopf falsch verankert (0 px hoch), und Horizons
   * Schliessen wartet auf eine Animation der Schublade, die unsichtbar nie laeuft. Darum steuert dieses Skript am Handy
   * Offen/Zu, Scroll-Sperre und Fokus selbst; am Computer bleibt Horizons Verhalten unberuehrt.
   * @param {HTMLDetailsElement} schublade
   */
  #handyMenueAnbinden(schublade) {
    const knopf = schublade.querySelector('summary');
    if (!knopf) return;
    // Capture am window: laeuft vor Horizons on:click-Verteilung (die am Dokument lauscht) und haelt sie an
    window.addEventListener('click', (e) => {
      if (!this.#handy.matches || !(e.target instanceof Element) || e.target.closest('summary') !== knopf) return;
      if (!this.#blattBauen()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      this.#blattUmschalten(schublade, !this.#blattOffen);
    }, { capture: true });
    document.addEventListener('keydown', (e) => {
      if (!this.#blattOffen) return;
      if (e.key === 'Escape') { this.#blattUmschalten(schublade, false); knopf.focus(); return; }
      // Tab bleibt zwischen Menue-Knopf und Blatt (wie ein Dialog)
      if (e.key !== 'Tab' || !this.#blatt) return;
      const ziele = [knopf, ...this.#blatt.querySelectorAll('a[href]')];
      const i = ziele.indexOf(/** @type {any} */ (document.activeElement));
      const n = i === -1 ? 0 : (i + (e.shiftKey ? -1 : 1) + ziele.length) % ziele.length;
      e.preventDefault();
      /** @type {HTMLElement} */ (ziele[n]).focus();
    });
    // Ins Computer-Format gedreht oder gezogen: Blatt schliessen
    this.#handy.addEventListener('change', (e) => { if (!e.matches && this.#blattOffen) this.#blattUmschalten(schublade, false); });
    // Seite wird ueber einen Link im Blatt verlassen und per Zurueck wiederhergestellt: geschlossen anzeigen
    window.addEventListener('pageshow', () => { if (this.#blattOffen) this.#blattUmschalten(schublade, false); });
  }

  /** @param {HTMLDetailsElement} schublade @param {boolean} auf */
  #blattUmschalten(schublade, auf) {
    const blatt = this.#blattBauen();
    if (!blatt || !this.#kopf) return;
    this.#blattOffen = auf;
    const knopf = schublade.querySelector('summary');
    knopf?.setAttribute('aria-expanded', String(auf));
    knopf?.setAttribute('aria-controls', blatt.id);
    // menu-open steuert Beschriftung ("Schliessen") und Symbol (Kreuz) des Knopfs; details bleibt zu
    schublade.classList.toggle('menu-open', auf);
    document.documentElement.classList.toggle('zw-blatt-offen', auf);
    blatt.toggleAttribute('data-offen', auf);
    blatt.inert = !auf;
    if (!auf) return;
    // Oberkante des Blatts = Unterkante des Kopfes; einige Bilder lang nachmessen (der Kopf kann noch einfahren)
    const messen = () => {
      const unten = Math.max(0, Math.round(this.#kopf?.getBoundingClientRect().bottom ?? 0));
      document.documentElement.style.setProperty('--zw-kopf-unten', unten + 'px');
    };
    messen();
    const ende = performance.now() + 500;
    const schritt = () => { messen(); if (this.#blattOffen && performance.now() < ende) requestAnimationFrame(schritt); };
    requestAnimationFrame(schritt);
  }

  /** @returns {HTMLElement | null} */
  #blattBauen() {
    if (this.#blatt) return this.#blatt;
    const tpl = this.querySelector('template[data-zw-blatt]');
    if (!(tpl instanceof HTMLTemplateElement) || !tpl.content.firstElementChild) return null;
    const blatt = /** @type {HTMLElement} */ (tpl.content.firstElementChild.cloneNode(true));
    blatt.id = 'zw-blatt';
    blatt.inert = true;
    document.body.append(blatt);
    this.#blatt = blatt;
    this.#zaehlerSpiegeln();
    return blatt;
  }

  /** Zaehler im grossen Knopf folgt Horizons Warenkorb-Blase */
  #zaehlerSpiegeln() {
    const blase = this.#kopf?.querySelector('.cart-bubble');
    if (!blase) return;
    const zeigen = () => {
      const zahl = (blase.querySelector('.cart-bubble__text-count')?.textContent || '').trim();
      for (const z of document.querySelectorAll('.zw-blatt [data-zw-zaehler]')) {
        z.textContent = zahl;
        z.toggleAttribute('hidden', zahl === '' || zahl === '0');
      }
    };
    zeigen();
    if (!blase.hasAttribute('data-zw-beobachtet')) {
      blase.setAttribute('data-zw-beobachtet', '');
      new MutationObserver(zeigen).observe(blase, { childList: true, subtree: true, characterData: true });
    }
  }
}

if (!customElements.get('zw-kopf-a')) customElements.define('zw-kopf-a', ZwKopfA);
